import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import FilialService from "../../../../services/resource/filialService";
import TipoProdutoService from "../../../../services/resource/tipoProdutoService";
import SolicitacaoComprasCampos, { calcularExigeVeiculo, validarCamposCompras } from "./SolicitacaoComprasCampos";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { mapearRateioParaPdf } from "../../../../utils/solicitacoes/orcamento";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";

const filialService = new FilialService();
const tipoProdutoService = new TipoProdutoService();

const inicial = {
    filialId: null,
    prioridade: null,
    tipoProdutoId: null,
    motivo: "",
    produtos: [],
    valorTotalEstimado: "",
    necessitaCotacao: true,
};

const cotacaoInicial = {
    filialEntregaId: null,
    fornecedores: [],
    observacao: "",
    valorFinalCotacao: "",
};

export default function NovaSolicitacaoComprasForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [cotacao, setCotacao] = useState(cotacaoInicial);
    const [rateio, setRateio] = useState([]);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, cotacao, rateio, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setCotacao(mesclarComPadrao(cotacaoInicial, dados.cotacao));
            setRateio(listaDoRascunho(dados.rateio));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [filiais, setFiliais] = useState([]);
    const [tiposProduto, setTiposProduto] = useState([]);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        tipoProdutoService.listarAtivos().then(res => setTiposProduto(res.data)).catch(() => setTiposProduto([]));
    }, []);

    // Imagem anexada a um produto (ver ProdutoEditor) é um anexo como outro qualquer assim que a
    // solicitação existir — mostra junto da lista de "Anexos" já durante o preenchimento, pra não
    // parecer que ela só existe depois de a solicitação ser criada. `form.produtos` continua sendo
    // a única fonte de verdade; isto é só uma projeção pra exibir + permitir remover por aqui também.
    const produtosComAnexo = form.produtos
        .map((p, indiceProduto) => ({ p, indiceProduto }))
        .filter(({ p }) => p.anexo);
    const anexosProdutos = produtosComAnexo.map(({ p }) => ({
        arquivo: p.anexo,
        descricao: `Produto: ${p.produtoCatalogoNome || "produto"}`,
    }));

    function removerAnexoProduto(indiceExtra) {
        const { indiceProduto } = produtosComAnexo[indiceExtra];
        setForm(prev => ({
            ...prev,
            produtos: prev.produtos.map((p, i) => i === indiceProduto ? { ...p, anexo: null } : p),
        }));
    }

    function validar() {
        const exigeVeiculo = calcularExigeVeiculo(form, tiposProduto);
        const erro = validarCamposCompras({ form, cotacao, rateio, exigeVeiculo });
        if (erro) {
            toast.error(erro);
            return false;
        }
        if (form.necessitaCotacao === false && anexos.length === 0) {
            toast.error("É necessário anexar a nota/cotação para concluir esta etapa.");
            return false;
        }
        if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        const etapaDestino = form.necessitaCotacao ? "ETAPA_2_APROVACAO_SOLICITACAO" : "ETAPA_4_APROVACAO_CORPORATIVA";
        // Contexto usado pelo preview do Sidebar de encaminhamento para resolver a atribuição
        // GRUPO_POR_CAMPO da Etapa 2 (grupo varia conforme a filial e o saldo das contas BP da
        // prévia de rateio) antes mesmo de a solicitação existir — ver
        // SolicitacaoCompraFluxo#camposParaCondicaoPreCriacao.
        const contextoCondicao = { filialId: form.filialId, baseBpIds: rateio.map(item => item.baseBpId).join(",") };
        let usuarioAprovadorId;
        try {
            usuarioAprovadorId = await encaminhar(etapaDestino, contextoCondicao);
        } catch {
            return;
        }

        const dadosCotacao = form.necessitaCotacao === false ? {
            filialEntregaId: cotacao.filialEntregaId,
            fornecedores: cotacao.fornecedores.map(f => ({
                nome: f.nome,
                produtosCotados: f.produtosCotados.map(l => ({
                    produtoId: l.produtoId,
                    quantidade: Number(l.quantidade),
                    valorUnitario: Number(l.valorUnitario),
                    prazoEntrega: l.prazoEntrega,
                })),
            })),
            observacao: cotacao.observacao,
            valorFinalCompra: Number(cotacao.valorFinalCotacao),
        } : {};

        setEnviando(true);
        service.criarSolicitacao({
            ...form,
            ...dadosCotacao,
            usuarioAprovadorId,
            produtos: form.produtos.map(p => ({
                produtoCatalogoId: p.produtoCatalogoId,
                quantidade: Number(p.quantidade),
                detalhes: p.detalhes,
                placaVeiculo: p.placaVeiculo,
            })),
            rateioOrcamentario: rateio.map(item => ({ baseBpId: item.baseBpId })),
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, [...anexos, ...anexosProdutos]),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
            rateioItens={mapearRateioParaPdf(rateio, "valor")}
            rateioItensDetalhados={rateio}
            anexosExtras={anexosProdutos}
            onRemoverAnexoExtra={removerAnexoProduto}
        >
            <SolicitacaoComprasCampos
                form={form}
                setForm={setForm}
                cotacao={cotacao}
                setCotacao={setCotacao}
                rateio={rateio}
                setRateio={setRateio}
                filiais={filiais}
                tiposProduto={tiposProduto}
            />

            <div className="flex justify-end">
                <Button variant="blue" label="Criar solicitação" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
